import { ReactNode, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaceIcon from '@mui/icons-material/Place';
import TabletIcon from '@mui/icons-material/Tablet';
import WhereToVoteOutlinedIcon from '@mui/icons-material/WhereToVoteOutlined';
import '@fontsource-variable/inter';
import { useAgency } from '../../hooks/useAgency';
import { useUser } from '../../hooks/useUser';
import type { WebApiResult } from '../../networkRequest/api';
import { AgenciesService } from '../../networkRequest/agenciesService';
import {
  type AgencyQuestionSummary,
  type Checkpoint,
  type CheckpointFields,
  type Partner,
  createCheckpoint,
  deleteCheckpoint,
  fetchCheckpoints,
  updateCheckpoint,
} from '../../networkRequest/checkpointsService';
import {
  type Location,
  type LocationFields,
  createLocation,
  deleteLocation,
  fetchLocations,
  updateLocation,
} from '../../networkRequest/locationsService';
import { surepassTheme } from '../../theme/surepass';
import CannotDeleteCheckpointDialog from './CannotDeleteCheckpointDialog';
import CannotDeleteLocationDialog from './CannotDeleteLocationDialog';
import CheckpointDialog from './CheckpointDialog';
import DeleteCheckpointDialog from './DeleteCheckpointDialog';
import DeleteLocationDialog from './DeleteLocationDialog';
import LocationDialog from './LocationDialog';

/**
 * The redesigned Checkpoints screen (ENG-849), ported from the design system's
 * `src/examples/Checkpoints.stories.tsx`, on the web API (/api/web/v1).
 *
 * Differences from the example, each from a component's CLAUDE.md:
 *   - The header panel and column layout are inline rather than the example's
 *     AppShell/PagePanel helpers; the app's own PageLayout provides the nav.
 *   - The header action is `Add`, not `New` (Button labels).
 *   - The tab bar has its bottom rule and goes full width below `sm` (Tabs).
 *   - Menu triggers carry `aria-controls`; delete items are styled destructive (Menu).
 *   - Headings step h2 → h3 → h4 without skipping a level (Heading).
 */

/** The Figma locations column is 280px; the detail takes the rest. */
const LOCATIONS_WIDTH = 280;

/** Adds a location when `locationId` is null, otherwise updates it. */
const saveLocation = async (
  accessToken: string,
  agencyId: number | string,
  locationId: number | null,
  fields: LocationFields,
): Promise<WebApiResult<Location>> => {
  let result: WebApiResult<Location>;
  if (locationId === null) {
    result = await createLocation(accessToken, agencyId, fields);
  } else {
    result = await updateLocation(accessToken, locationId, fields);
  }
  return result;
};

/** Adds a checkpoint at the location when `checkpoint` is null, otherwise updates it. */
const saveCheckpoint = async (
  accessToken: string,
  locationId: number,
  checkpoint: Checkpoint | null,
  fields: CheckpointFields,
): Promise<WebApiResult<Checkpoint>> => {
  let result: WebApiResult<Checkpoint>;
  if (checkpoint === null) {
    result = await createCheckpoint(accessToken, locationId, fields);
  } else {
    result = await updateCheckpoint(accessToken, checkpoint.id, fields);
  }
  return result;
};

/**
 * The list with `saved` in it, replacing the item with its id if there is one,
 * in name order like the API returns them.
 */
const withSaved = <T extends { id: number; name: string }>(items: T[], saved: T): T[] =>
  [...items.filter((item) => item.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name));

/** The checkpoint and location ⋮ buttons' ids, so focus can move to one after a delete. */
const getCheckpointActionsId = (checkpointId: number) => `checkpoint-actions-${checkpointId}`;
const LOCATION_ACTIONS_ID = 'location-actions';

/** A message where a list would be: loading, empty, or nothing to show yet. */
const MessageCard = ({ children, role }: { children: ReactNode; role?: 'status' }) => (
  <Card variant="outlined">
    <Typography variant="body2" role={role} sx={{ color: 'text.secondary', p: 2 }}>
      {children}
    </Typography>
  </Card>
);

/** The chips under a checkpoint's name: PIN, Check In/Out, then how many questions are on. */
const getCapabilities = (checkpoint: Checkpoint): string[] => {
  const capabilities: string[] = [];
  if (checkpoint.pin_required) {
    capabilities.push('PIN');
  }
  if (checkpoint.check_in_enabled) {
    capabilities.push('Check In/Out');
  }
  const enabledCount = checkpoint.checkpoint_rules.filter((rule) => rule.enabled).length;
  capabilities.push(`${enabledCount}/${checkpoint.checkpoint_rules.length} Questions`);
  return capabilities;
};

/** One line: street, optional second line, then city, state and postal code. */
const formatAddress = (location: Location): string =>
  [
    location.street_address_1,
    location.street_address_2,
    `${location.city}, ${location.state} ${location.postal}`,
  ]
    .filter(Boolean)
    .join(', ');

/** The distinct partners among these checkpoints, in the order they first appear. */
const getPartners = (checkpoints: Checkpoint[]): Partner[] => {
  const partnersById = new Map<string, Partner>();
  checkpoints.forEach((checkpoint) => {
    if (checkpoint.partner) {
      partnersById.set(checkpoint.partner.id, checkpoint.partner);
    }
  });
  return [...partnersById.values()];
};

type DeviceTableProps = { checkpoint: Checkpoint };

/** The devices under a checkpoint, collapsed by default behind its own header. */
const DeviceTable = ({ checkpoint }: DeviceTableProps) => {
  const [open, setOpen] = useState(false);
  const panelId = `devices-${checkpoint.id}`;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
      {/* Neutral skin from the Accordion page: action.hover tints both schemes. */}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          minHeight: 44,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle1" component="h4">
          Devices
        </Typography>
        <Tooltip title={`${open ? 'Hide' : 'Show'} devices for ${checkpoint.name}`} describeChild>
          <IconButton
            size="small"
            aria-label={`${open ? 'Hide' : 'Show'} devices for ${checkpoint.name}`}
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen(!open)}
          >
            <ExpandMoreIcon
              sx={(theme) => ({
                transform: open ? 'rotate(180deg)' : 'none',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                }),
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              })}
            />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={open} id={panelId}>
        <Divider />
        <TableContainer sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <Table size="small" aria-label={`Devices at ${checkpoint.name}`}>
            <TableHead>
              <TableRow>
                <TableCell>Device Name</TableCell>
                <TableCell>Device ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checkpoint.device_configurations.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                      <TabletIcon fontSize="small" color="action" />
                      {device.device_name}
                    </Stack>
                  </TableCell>
                  <TableCell>{device.device_uuid}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

type CheckpointRowProps = {
  checkpoint: Checkpoint;
  locationName: string;
  /** Every question the agency has, for the edit dialog. */
  agencyQuestions: AgencyQuestionSummary[];
  onSave: (checkpoint: Checkpoint, fields: CheckpointFields) => Promise<WebApiResult<Checkpoint>>;
  onDelete: (checkpoint: Checkpoint) => Promise<WebApiResult<never>>;
};

/** One checkpoint: the name, what it can do, and the devices standing at it. */
const CheckpointRow = ({
  checkpoint,
  locationName,
  agencyQuestions,
  onSave,
  onDelete,
}: CheckpointRowProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const menuId = `checkpoint-menu-${checkpoint.id}`;
  const closeMenu = () => setMenuAnchor(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  // Bumped on every open, so the dialog remounts with a fresh form.
  const [editDialogKey, setEditDialogKey] = useState(0);

  const openEditDialog = () => {
    closeMenu();
    setEditDialogKey(editDialogKey + 1);
    setIsEditDialogOpen(true);
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const openDeleteDialog = () => {
    closeMenu();
    setIsDeleteDialogOpen(true);
  };

  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between', gap: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3">{checkpoint.name}</Typography>
          {checkpoint.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {checkpoint.description}
            </Typography>
          )}
        </Box>

        <Stack direction="row" useFlexGap sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {getCapabilities(checkpoint).map((capability) => (
            <Chip key={capability} label={capability} size="small" />
          ))}
          <Tooltip title={`Actions for ${checkpoint.name}`} describeChild>
            <IconButton
              id={getCheckpointActionsId(checkpoint.id)}
              size="small"
              aria-label={`Actions for ${checkpoint.name}`}
              aria-haspopup="true"
              aria-controls={menuOpen ? menuId : undefined}
              aria-expanded={menuOpen ? 'true' : undefined}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <DeviceTable checkpoint={checkpoint} />

      <Menu id={menuId} anchorEl={menuAnchor} open={menuOpen} onClose={closeMenu}>
        <MenuItem onClick={openEditDialog}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Checkpoint</ListItemText>
        </MenuItem>
        <MenuItem onClick={openDeleteDialog} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Checkpoint</ListItemText>
        </MenuItem>
      </Menu>

      <CheckpointDialog
        key={editDialogKey}
        open={isEditDialogOpen}
        checkpoint={checkpoint}
        locationName={locationName}
        agencyQuestions={agencyQuestions}
        onSave={(fields) => onSave(checkpoint, fields)}
        onClose={() => setIsEditDialogOpen(false)}
      />

      {/* Partner checkpoints can only be deleted by a SurePass administrator. */}
      {checkpoint.partner ? (
        <CannotDeleteCheckpointDialog
          open={isDeleteDialogOpen}
          checkpoint={checkpoint}
          onClose={() => setIsDeleteDialogOpen(false)}
        />
      ) : (
        <DeleteCheckpointDialog
          open={isDeleteDialogOpen}
          checkpoint={checkpoint}
          onDelete={() => onDelete(checkpoint)}
          onClose={() => setIsDeleteDialogOpen(false)}
        />
      )}
    </Stack>
  );
};

type CheckpointsContentProps = {
  accessToken: string;
  agencyId: number | string;
};

/** The screen itself, once the signed-in user and their agency are known. */
const CheckpointsContent = ({ accessToken, agencyId }: CheckpointsContentProps) => {
  // null while loading.
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [agencyQuestions, setAgencyQuestions] = useState<AgencyQuestionSummary[]>([]);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  // The active location's checkpoints; null while loading.
  const [locationCheckpoints, setLocationCheckpoints] = useState<Checkpoint[] | null>(null);
  const [hasCheckpointsError, setHasCheckpointsError] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [locationMenuAnchor, setLocationMenuAnchor] = useState<HTMLElement | null>(null);
  // null is the agency's own checkpoints; otherwise the partner whose tab is chosen.
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  // Kept separate from `open`, so the title doesn't flip to "Add" while the dialog fades out.
  const [dialogLocation, setDialogLocation] = useState<Location | null>(null);
  // Bumped on every open, so the dialog remounts with a fresh form and no leftover errors.
  const [locationDialogKey, setLocationDialogKey] = useState(0);
  const [isDeleteLocationDialogOpen, setIsDeleteLocationDialogOpen] = useState(false);
  // The location being deleted and its checkpoints, kept apart from the selection: a
  // delete selects the next location while the dialog is still fading out.
  const [locationToDelete, setLocationToDelete] = useState<{
    location: Location;
    checkpoints: Checkpoint[];
  } | null>(null);
  const [isAddCheckpointDialogOpen, setIsAddCheckpointDialogOpen] = useState(false);
  // Bumped on every open, so the dialog remounts with a fresh form.
  const [addCheckpointDialogKey, setAddCheckpointDialogKey] = useState(0);
  // `variant` is a prop, so it can't take an sx breakpoint object (Tabs page).
  const isMobile = useMediaQuery(surepassTheme.breakpoints.down('sm'));
  // Snackbar page: one at a time, so a second confirmation replaces the first.
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // The agency's locations and questions. The first location is selected, unless
  // one already is (the access token changes when it's refreshed, which reloads these).
  useEffect(() => {
    const load = async () => {
      const [locationsResult, questionsResult] = await Promise.all([
        fetchLocations(accessToken, agencyId),
        // For the checkpoint dialog's question list. The web API has no endpoint for these yet.
        AgenciesService.getAgencyQuestions(accessToken, agencyId),
      ]);
      if (locationsResult.data && questionsResult.data) {
        const firstLocationId = locationsResult.data[0]?.id ?? null;
        setLocations(locationsResult.data);
        setAgencyQuestions(questionsResult.data);
        setActiveId((currentId) => currentId ?? firstLocationId);
      } else {
        setHasLoadError(true);
      }
    };
    load();
  }, [accessToken, agencyId]);

  // The active location's checkpoints, whenever it changes.
  useEffect(() => {
    if (activeId === null) {
      return;
    }
    // Set by the cleanup, so a slow response for a location no longer selected is dropped.
    let isStale = false;
    setLocationCheckpoints(null);
    setHasCheckpointsError(false);
    const load = async () => {
      const result = await fetchCheckpoints(accessToken, activeId);
      if (isStale) {
        return;
      }
      if (result.data) {
        setLocationCheckpoints(result.data);
      } else {
        setHasCheckpointsError(true);
      }
    };
    load();
    return () => {
      isStale = true;
    };
  }, [accessToken, activeId]);

  const active = locations?.find((location) => location.id === activeId) ?? null;
  const loadedCheckpoints = locationCheckpoints ?? [];
  // One tab per partner at this location. The bar only shows when there is more than one view.
  const partners = getPartners(loadedCheckpoints);
  const showTabs = partners.length > 0;
  // Falls back to the agency's own checkpoints when this location lacks the chosen partner.
  const selectedPartner = partners.find((partner) => partner.id === selectedPartnerId) ?? null;
  const tabValue = selectedPartner?.id ?? null;
  const checkpoints = loadedCheckpoints.filter(
    (checkpoint) => (checkpoint.partner?.id ?? null) === tabValue,
  );

  let emptyMessage = `No checkpoints at ${active?.name}.`;
  if (selectedPartner) {
    emptyMessage = `No ${selectedPartner.name} checkpoints at ${active?.name}.`;
  }

  /** Saves the location in the dialog, then shows it, selected, in the list. */
  const handleSaveLocation = async (fields: LocationFields) => {
    const result = await saveLocation(accessToken, agencyId, dialogLocation?.id ?? null, fields);
    const saved = result.data;
    if (saved) {
      setLocations((current) => withSaved(current ?? [], saved));
      setActiveId(saved.id);
      setSnackbarMessage(dialogLocation ? `Saved changes to ${saved.name}` : `Added ${saved.name}`);
    }
    return result;
  };

  /** Saves a checkpoint at the location, then shows it in the list. */
  const handleSaveCheckpoint = async (
    locationId: number,
    checkpoint: Checkpoint | null,
    fields: CheckpointFields,
  ) => {
    const result = await saveCheckpoint(accessToken, locationId, checkpoint, fields);
    const saved = result.data;
    if (saved) {
      setLocationCheckpoints((current) => withSaved(current ?? [], saved));
      if (checkpoint) {
        setSnackbarMessage(`Saved changes to ${saved.name}`);
      } else {
        // A new checkpoint is the agency's own, so show that tab or it can't be seen.
        setSelectedPartnerId(null);
        setSnackbarMessage(`Added ${saved.name}`);
      }
    }
    return result;
  };

  /** Deletes the location, and its checkpoints with it, then selects the first location left. */
  const handleDeleteLocation = async (location: Location) => {
    const result = await deleteLocation(accessToken, location.id);
    if (!result.error) {
      const remaining = (locations ?? []).filter((item) => item.id !== location.id);
      setLocations(remaining);
      // Cleared now rather than when the next location's load starts, so nothing of the
      // deleted location's (its partner tabs) shows if no location is left to select.
      setLocationCheckpoints(null);
      setActiveId(remaining[0]?.id ?? null);
      setSnackbarMessage(`Deleted ${location.name}`);
    }
    return result;
  };

  /**
   * Deletes the checkpoint, then moves focus forward: to the next checkpoint's ⋮
   * button, or the previous one's if it was last, or the location's if none are left.
   * Its own ⋮ button goes with it, so focus would otherwise fall to the page.
   */
  const handleDeleteCheckpoint = async (checkpoint: Checkpoint) => {
    const result = await deleteCheckpoint(accessToken, checkpoint.id);
    if (!result.error) {
      const index = checkpoints.findIndex((item) => item.id === checkpoint.id);
      const nextCheckpoint = checkpoints[index + 1] ?? checkpoints[index - 1];
      let focusId = LOCATION_ACTIONS_ID;
      if (nextCheckpoint) {
        focusId = getCheckpointActionsId(nextCheckpoint.id);
      }
      // Render the list without it first, so the button to focus is the one left standing.
      flushSync(() => {
        setLocationCheckpoints((current) => (current ?? []).filter((item) => item.id !== checkpoint.id));
      });
      document.getElementById(focusId)?.focus();
      setSnackbarMessage(`Deleted ${checkpoint.name}`);
    }
    return result;
  };

  const closeAddMenu = () => setAddMenuAnchor(null);
  const closeLocationMenu = () => setLocationMenuAnchor(null);

  const openAddCheckpointDialog = () => {
    closeAddMenu();
    setAddCheckpointDialogKey(addCheckpointDialogKey + 1);
    setIsAddCheckpointDialogOpen(true);
  };

  const openAddLocationDialog = () => {
    closeAddMenu();
    setDialogLocation(null);
    setLocationDialogKey(locationDialogKey + 1);
    setIsLocationDialogOpen(true);
  };

  const openEditLocationDialog = () => {
    closeLocationMenu();
    setDialogLocation(active);
    setLocationDialogKey(locationDialogKey + 1);
    setIsLocationDialogOpen(true);
  };

  const openDeleteLocationDialog = () => {
    closeLocationMenu();
    // The menu item is disabled until both are loaded.
    if (active && locationCheckpoints) {
      setLocationToDelete({ location: active, checkpoints: locationCheckpoints });
    }
    setIsDeleteLocationDialogOpen(true);
  };

  // Fills PageLayout's main area: a locked header panel, then a region that
  // scrolls in the detail column from md up and as one page below it.
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
        color: 'text.primary',
        fontFamily: surepassTheme.typography.fontFamily,
      }}
    >
      {/* Header panel (Panel + PageHeader pages): the divider is its bottom edge. */}
      <Paper square elevation={0} sx={{ bgcolor: 'background.paper' }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}
        >
          <Typography variant="h1">Checkpoints</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            aria-haspopup="true"
            aria-controls={addMenuAnchor ? 'add-menu' : undefined}
            aria-expanded={addMenuAnchor ? 'true' : undefined}
            onClick={(event) => setAddMenuAnchor(event.currentTarget)}
          >
            Add
          </Button>
        </Stack>
        <Divider />
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: { xs: 'block', md: 'flex' },
          overflowY: { xs: 'auto', md: 'hidden' },
        }}
      >
        {/* Locations panel: one stroke, on the edge that meets the detail. Longhand
            borders, because a responsive shorthand resets the colour. */}
        <Paper
          square
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            width: { md: LOCATIONS_WIDTH },
            flexShrink: 0,
            borderStyle: 'solid',
            borderColor: 'divider',
            borderWidth: 0,
            borderRightWidth: { xs: 0, md: '1px' },
            borderBottomWidth: { xs: '1px', md: 0 },
            overflowY: { md: 'auto' },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="h2" id="locations-heading">
              Locations
            </Typography>
          </Box>
          {locations === null && !hasLoadError && (
            <Typography variant="body2" role="status" sx={{ color: 'text.secondary', px: 2, pb: 2 }}>
              Loading locations…
            </Typography>
          )}
          <List aria-labelledby="locations-heading" sx={{ px: 1 }}>
            {(locations ?? []).map((location) => (
              <ListItem key={location.id} disablePadding>
                <ListItemButton
                  selected={location.id === activeId}
                  aria-current={location.id === activeId ? 'true' : undefined}
                  onClick={() => setActiveId(location.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText primary={location.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* The one region that scrolls. A Box scrolls; the Stack inside only lays out. */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: { md: 'auto' } }}>
          <Stack spacing={2} sx={{ p: 2 }}>
            {hasLoadError && (
              <Alert severity="error">
                Your locations couldn&apos;t be loaded. Refresh the page to try again.
              </Alert>
            )}

            {locations?.length === 0 && (
              <MessageCard>Your agency has no locations yet. Use Add to create one.</MessageCard>
            )}

            {active && (
              <Card variant="outlined">
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: 'flex-start', justifyContent: 'space-between', p: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h2">{active.name}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }}>
                      <PlaceIcon fontSize="small" color="action" aria-hidden="true" />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {formatAddress(active)}
                      </Typography>
                    </Stack>
                  </Box>
                  <Tooltip title={`Actions for ${active.name}`} describeChild>
                    <IconButton
                      id={LOCATION_ACTIONS_ID}
                      aria-label={`Actions for ${active.name}`}
                      aria-haspopup="true"
                      aria-controls={locationMenuAnchor ? 'location-menu' : undefined}
                      aria-expanded={locationMenuAnchor ? 'true' : undefined}
                      onClick={(event) => setLocationMenuAnchor(event.currentTarget)}
                      sx={{ flexShrink: 0 }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Card>
            )}

            {active && hasCheckpointsError && (
              <Alert severity="error">
                Checkpoints couldn&apos;t be loaded. Refresh the page to try again.
              </Alert>
            )}

            {active && locationCheckpoints === null && !hasCheckpointsError && (
              <MessageCard role="status">Loading checkpoints…</MessageCard>
            )}

            {/* Page navigation: sized to its tabs from sm up, full width below it. */}
            {showTabs && (
              <Tabs
                value={tabValue}
                onChange={(_, next: string | null) => setSelectedPartnerId(next)}
                aria-label="Checkpoint groups"
                variant={isMobile ? 'fullWidth' : 'standard'}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                <Tab
                  label="Checkpoints"
                  value={null}
                  id="checkpoint-tab-own"
                  aria-controls="checkpoint-panel-own"
                />
                {partners.map((partner) => (
                  <Tab
                    key={partner.id}
                    label={`${partner.name} Checkpoints`}
                    value={partner.id}
                    id={`checkpoint-tab-${partner.id}`}
                    aria-controls={`checkpoint-panel-${partner.id}`}
                  />
                ))}
              </Tabs>
            )}

            {active && locationCheckpoints && (
              <Stack
                spacing={2}
                role={showTabs ? 'tabpanel' : undefined}
                id={showTabs ? `checkpoint-panel-${tabValue ?? 'own'}` : undefined}
                aria-labelledby={showTabs ? `checkpoint-tab-${tabValue ?? 'own'}` : undefined}
              >
                {checkpoints.length === 0 ? (
                  <MessageCard>{emptyMessage}</MessageCard>
                ) : (
                  checkpoints.map((checkpoint) => (
                    <Card variant="outlined" key={checkpoint.id}>
                      <CheckpointRow
                        checkpoint={checkpoint}
                        locationName={active.name}
                        agencyQuestions={agencyQuestions}
                        onSave={(checkpoint, fields) =>
                          handleSaveCheckpoint(checkpoint.location_id, checkpoint, fields)
                        }
                        onDelete={handleDeleteCheckpoint}
                      />
                    </Card>
                  ))
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>

      <Menu id="add-menu" anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={closeAddMenu}>
        {/* A checkpoint is added to the selected location, so there has to be one. */}
        <MenuItem onClick={openAddCheckpointDialog} disabled={!active}>
          <ListItemIcon>
            <WhereToVoteOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Checkpoint</ListItemText>
        </MenuItem>
        <MenuItem onClick={openAddLocationDialog}>
          <ListItemIcon>
            <PlaceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Location</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        id="location-menu"
        anchorEl={locationMenuAnchor}
        open={Boolean(locationMenuAnchor)}
        onClose={closeLocationMenu}
      >
        <MenuItem onClick={openEditLocationDialog}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Location</ListItemText>
        </MenuItem>
        {/* What deleting does depends on the location's checkpoints, so they have to be loaded. */}
        <MenuItem
          onClick={openDeleteLocationDialog}
          disabled={locationCheckpoints === null}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Location</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar page: bottom left on desktop, bottom centre on a phone, and
          role="status" so a routine confirmation waits for a pause rather than interrupting. */}
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'left' }}
        slotProps={{ content: { role: 'status' } }}
      />

      {/* Adds to the selected location; the menu item is disabled until there is one. */}
      {active && (
        <CheckpointDialog
          key={`add-checkpoint-${addCheckpointDialogKey}`}
          open={isAddCheckpointDialogOpen}
          checkpoint={null}
          locationName={active.name}
          agencyQuestions={agencyQuestions}
          onSave={(fields) => handleSaveCheckpoint(active.id, null, fields)}
          onClose={() => setIsAddCheckpointDialogOpen(false)}
        />
      )}

      <LocationDialog
        key={`location-${locationDialogKey}`}
        open={isLocationDialogOpen}
        location={dialogLocation}
        onSave={handleSaveLocation}
        onClose={() => setIsLocationDialogOpen(false)}
      />

      {/* Deleting a location deletes its checkpoints too, so it's blocked while it has
          partner checkpoints, which only a SurePass administrator can delete. */}
      {locationToDelete && getPartners(locationToDelete.checkpoints).length > 0 && (
        <CannotDeleteLocationDialog
          open={isDeleteLocationDialogOpen}
          location={locationToDelete.location}
          partnerCheckpoints={locationToDelete.checkpoints.filter((checkpoint) => checkpoint.partner)}
          onClose={() => setIsDeleteLocationDialogOpen(false)}
        />
      )}
      {locationToDelete && getPartners(locationToDelete.checkpoints).length === 0 && (
        <DeleteLocationDialog
          open={isDeleteLocationDialogOpen}
          location={locationToDelete.location}
          checkpointCount={locationToDelete.checkpoints.length}
          onDelete={() => handleDeleteLocation(locationToDelete.location)}
          onClose={() => setIsDeleteLocationDialogOpen(false)}
        />
      )}
    </Box>
  );
};

/**
 * Waits for the signed-in user and their agency, which load just after sign-in,
 * so everything inside can rely on them.
 */
const CheckpointsPage = () => {
  const { user } = useUser();
  const { agency } = useAgency();

  return (
    <ThemeProvider theme={surepassTheme} defaultMode="light">
      {user && agency && <CheckpointsContent accessToken={user.accessToken} agencyId={agency.id} />}
    </ThemeProvider>
  );
};

export default CheckpointsPage;
